import { flexRender, type Row } from '@tanstack/react-table';
import { memo, type CSSProperties, type HTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type MutableRefObject, type ReactNode } from 'react';
import {
  consumeDataTableRowClickSuppression,
  isDataTableRowPointerBlocked,
} from '../useDataTableRowHorizontalScroll.js';
import { usePressFeedbackClick } from '../../hooks/usePressFeedbackClick.js';
import { DataTableRowActionsContent } from './DataTableRowActions.js';
import { DataTableRowSelectControl } from './DataTableRowSelectControl.js';
import { getDataTableCellProps, wpPinToCellPin, type DataTableDensity } from './dataTableCell.js';
import { wrapStickyCellContent } from './dataTableStickyContent.js';
import { wpPinSide } from './useDataTableCore.js';
import type { Column, DataTableSelection } from './types.js';
import styles from './DataTable.module.css';
import { cx } from '../../utils/cx.js';

export type RenderDataTableDataRowOptions<T> = {
  tanRow: Row<T>;
  rowIndex: number;
  density: DataTableDensity;
  compactActions: boolean;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  selection?: DataTableSelection<T>;
  firstDataColumnKey?: string;
  onToggleRowSelection?: (key: string) => void;
  onRowClick?: (row: T) => void;
  isRowClickable?: (row: T) => boolean;
  onRowPointerEnter?: (row: T) => void;
  proximityHover?: boolean;
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement> | undefined;
  rowTrWrapper?: (
    row: T,
    tr: ReactNode,
    ctx: { rowIndex: number; sortableId: string },
  ) => ReactNode;
  rowKey: (row: T) => string;
  mergeCellStyles: (
    columnId: string,
    declarativeWidth?: string,
    isHeader?: boolean,
  ) => CSSProperties | undefined;
  suppressRowClickRef: MutableRefObject<boolean>;
  virtualRowStyle?: CSSProperties;
  measureRef?: (node: HTMLTableRowElement | null) => void;
};

function DataTableDataRow<T>({
  tanRow,
  rowIndex,
  density: _density,
  compactActions,
  rowLeadingActions,
  rowActions,
  selection,
  firstDataColumnKey,
  onToggleRowSelection,
  onRowClick,
  isRowClickable,
  onRowPointerEnter,
  proximityHover = false,
  rowProps,
  rowTrWrapper,
  rowKey,
  mergeCellStyles,
  suppressRowClickRef,
  virtualRowStyle,
  measureRef,
}: RenderDataTableDataRowOptions<T>): ReactNode {
  const row = tanRow.original;
  const extra = rowProps?.(row);
  const rowClickable =
    Boolean(extra?.onClick) ||
    (Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true));
  const rowClick = rowClickable ? onRowClick : undefined;
  const extraOnClick = extra?.onClick;
  const mergedOnClick =
    rowClick || extraOnClick
      ? (e: ReactMouseEvent<HTMLTableRowElement>) => {
          extraOnClick?.(e);
          if (consumeDataTableRowClickSuppression(suppressRowClickRef)) return;
          if (!rowClick) return;
          if (e.defaultPrevented) return;
          if (isDataTableRowPointerBlocked(e.target)) return;
          rowClick(row);
        }
      : undefined;
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(mergedOnClick, {
    disabled: !mergedOnClick,
  });
  const mergedOnKeyDown = rowClick
    ? (e: ReactKeyboardEvent<HTMLTableRowElement>) => {
        extra?.onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const t = e.target;
        if (t instanceof Element && isDataTableRowPointerBlocked(t)) return;
        e.preventDefault();
        rowClick(row);
      }
    : extra?.onKeyDown;
  const { onKeyDown: _extraKeyDown, onClick: _extraOnClick, onPointerEnter: extraOnPointerEnter, ...extraRest } =
    extra ?? {};
  const mergedOnPointerEnter =
    onRowPointerEnter || extraOnPointerEnter
      ? (e: ReactPointerEvent<HTMLTableRowElement>) => {
          extraOnPointerEnter?.(e);
          onRowPointerEnter?.(row);
        }
      : undefined;
  const rowSelectKey = rowKey(row);

  const tr = (
    <tr
      key={tanRow.id}
      ref={measureRef}
      data-index={rowIndex}
      {...(proximityHover && rowClickable ? { 'data-wp-proximity-item': '' } : {})}
      {...extraRest}
      tabIndex={rowClickable ? 0 : extra?.tabIndex}
      className={cx(styles.tr, rowClickable && styles.trClickable, extra?.className)}
      style={virtualRowStyle}
      onClick={mergedOnClick ? pressOnClick : undefined}
      {...(mergedOnClick ? pressAttrs : {})}
      onPointerEnter={mergedOnPointerEnter}
      onKeyDown={mergedOnKeyDown}
    >
      {tanRow.getVisibleCells().map((cell) => {
        if (cell.column.id === '__actions') {
          return (
            <DataTableRowActionsContent
              key={cell.id}
              leading={rowLeadingActions?.(row)}
              menu={rowActions?.(row)}
              compact={compactActions}
              stickyEnd={cell.column.getIsPinned() === 'right'}
              columnWidthStyle={mergeCellStyles('__actions', undefined, false)}
            />
          );
        }
        const wpCol = (cell.column.columnDef.meta as { wpColumn?: Column<T> } | undefined)?.wpColumn;
        const pin = wpPinSide(cell.column.getIsPinned());
        const isLeadCol = Boolean(selection && firstDataColumnKey && cell.column.id === firstDataColumnKey);
        const selectable = selection?.isRowSelectable?.(row) !== false;
        const bodyCell = getDataTableCellProps({
          element: 'td',
          part: 'body',
          pin: wpPinToCellPin(pin),
          extraClassName: cx(wpCol?.columnClassName, isLeadCol && styles.tdHasRowSelect),
        });
        const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
        return (
          <td
            key={cell.id}
            {...bodyCell}
            style={{
              ...mergeCellStyles(cell.column.id, wpCol?.width),
              ...(wpCol?.columnStyle ?? {}),
            }}
          >
            {isLeadCol ? (
              <DataTableRowSelectControl
                checked={selection?.selectedKeys.has(rowSelectKey) ?? false}
                disabled={!selectable}
                ariaLabel={`Sélectionner la ligne ${rowSelectKey}`}
                onChange={() => onToggleRowSelection?.(rowSelectKey)}
              />
            ) : null}
            {wrapStickyCellContent(cellContent, wpPinToCellPin(pin), {
              ellipsis: wpCol?.stickyContentEllipsis !== false,
            })}
          </td>
        );
      })}
    </tr>
  );

  if (rowTrWrapper) {
    return rowTrWrapper(row, tr, { rowIndex, sortableId: rowKey(row) });
  }
  return tr;
}

export const renderDataTableDataRow = memo(DataTableDataRow) as typeof DataTableDataRow;
